// src/shared/ui/DataTable/index.jsx
//
// Promoted from golive/components/DataTable/index.jsx — the only table
// implementation in the app that properly wraps @tanstack/react-table with
// sorting, pagination and search, instead of hand-rolling a <table> per
// page. codebase/components/FindingsTable.jsx and IT/components/ActivityTable.jsx
// used to reimplement plain <table> markup with none of that; both now
// build a `{ columns, rows }` shape and render this instead (see their
// files for the column definitions).
//
// Usage:
//   <DataTable table={{ columns, rows }} title="Findings" canSearch />

import { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import DataTableHeadCell from "./DataTableHeadCell";
import DataTableBodyCell from "./DataTableBodyCell";

function skeletonBarWidth(col) {
  return typeof col.width === "number" ? `${Math.max(col.width * 0.6, 24)}px` : "60%";
}

export default function DataTable({
  title = null,
  entriesPerPage = { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch = false,
  showTotalEntries = true,
  table,
  isSorted = true,
  noEndBorder = false,
  toolbarContent = null,
  maxTableHeight = null,
  autoResetPageIndex = true,
  emptyMessage = "No data to display.",
  loading = false,
}) {
  const defaultValue = entriesPerPage?.defaultValue ?? 10;
  const entrySizes = entriesPerPage?.entries ?? [5, 10, 15, 20, 25];

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: defaultValue });

  useEffect(() => {
    const t = setTimeout(() => setGlobalFilter(searchInput || ""), 100);
    return () => clearTimeout(t);
  }, [searchInput]);

  const instance = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter, pagination },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    autoResetPageIndex,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const rows = instance.getRowModel().rows;
  const totalRows = instance.getFilteredRowModel().rows.length;
  const pageIndex = instance.getState().pagination.pageIndex;
  const pageSize = instance.getState().pagination.pageSize;
  const pageCount = instance.getPageCount();

  const entriesStart = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const entriesEnd = Math.min(entriesStart + pageSize - 1, totalRows);
  const skeletonRowCount = data.length > 0 ? data.length : pageSize || 5;

  return (
    <div className="flex flex-col" style={maxTableHeight ? { maxHeight: maxTableHeight, overflow: "auto" } : {}}>
      {(title || canSearch || toolbarContent) && (
        <div className="flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-gray-100">
          {title && <p className="text-base font-semibold text-slate-800">{title}</p>}
          <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
            {canSearch && (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="gl-input pl-8 w-48 text-sm"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            )}
            {toolbarContent}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {instance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <DataTableHeadCell
                    key={header.id}
                    width={header.column.columnDef.width ?? "auto"}
                    align={header.column.columnDef.align ?? "left"}
                    sorted={isSorted && header.column.getCanSort() ? header.column.getIsSorted() : undefined}
                    sortAllowed={header.column.columnDef.sortAllowed !== false}
                    onClick={isSorted ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </DataTableHeadCell>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: skeletonRowCount }, (_, rowIdx) => (
                <tr key={`skeleton-row-${rowIdx}`}>
                  {columns.map((col, colIdx) => (
                    <DataTableBodyCell
                      key={col.id ?? col.accessorKey ?? colIdx}
                      align={col.align ?? "left"}
                      noBorder={noEndBorder && rowIdx === skeletonRowCount - 1}
                    >
                      <div
                        className="h-3.5 rounded bg-gray-200 animate-pulse"
                        style={{ width: skeletonBarWidth(col) }}
                      />
                    </DataTableBodyCell>
                  ))}
                </tr>
              ))
            ) : (
              <>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="py-6 text-center text-sm text-slate-400">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
                {rows.map((row, rowIdx) => (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-100">
                    {row.getVisibleCells().map((cell) => (
                      <DataTableBodyCell
                        key={cell.id}
                        align={cell.column.columnDef.align ?? "left"}
                        noBorder={noEndBorder && rowIdx === rows.length - 1}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </DataTableBodyCell>
                    ))}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {entriesPerPage !== false && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-t border-gray-100">
          {entriesPerPage && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <select
                className="gl-input py-1 w-20 text-sm"
                value={pageSize}
                onChange={(e) => instance.setPageSize(Number(e.target.value))}
              >
                {entrySizes.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <span>entries per page</span>
            </div>
          )}

          {showTotalEntries && (
            <p className="text-sm text-slate-500">
              Showing {entriesStart} to {entriesEnd} of {totalRows} entries
            </p>
          )}

          {pageCount > 1 && (
            <div className="flex items-center gap-1 sm:ml-auto">
              <button className="gl-btn px-2 py-1 disabled:opacity-40" onClick={() => instance.previousPage()} disabled={!instance.getCanPreviousPage()}>
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: pageCount }, (_, i) => (
                <button
                  key={i}
                  className={[
                    "px-3 py-1 rounded text-sm font-medium transition-colors border-0 outline-none",
                    pageIndex === i ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100",
                  ].join(" ")}
                  onClick={() => instance.setPageIndex(i)}
                >
                  {i + 1}
                </button>
              ))}
              <button className="gl-btn px-2 py-1 disabled:opacity-40" onClick={() => instance.nextPage()} disabled={!instance.getCanNextPage()}>
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({ defaultValue: PropTypes.number, entries: PropTypes.arrayOf(PropTypes.number) }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.shape({ columns: PropTypes.array.isRequired, rows: PropTypes.array.isRequired }).isRequired,
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  title: PropTypes.string,
  toolbarContent: PropTypes.node,
  maxTableHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  emptyMessage: PropTypes.node,
  loading: PropTypes.bool,
};
